import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, Eye, EyeOff, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/errors";
import api from "@/lib/api";

export default function Pro2FADisableModal({ open, onClose, onComplete }) {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setPassword("");
      setShowPassword(false);
      setCode("");
      setLoading(false);
    }
  }, [open]);

  const handleDisable = async (e) => {
    e.preventDefault();
    if (!password || code.length !== 6) return;
    setLoading(true);
    try {
      await api.post("/pro/2fa/disable", { password, code });
      toast.success("Two-factor authentication disabled");
      onComplete?.();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to disable 2FA"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="sm:max-w-[400px]" data-testid="pro-2fa-disable-modal">
        <DialogHeader>
          <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center mb-2">
            <ShieldOff size={20} className="text-red-600" />
          </div>
          <DialogTitle className="text-base">Disable two-factor authentication</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            This will remove the TOTP requirement from your account. You'll need your password and a current authenticator code.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleDisable} className="space-y-3 pt-2">
          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-500">Password</Label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your password"
                className="pr-10"
                autoFocus
                data-testid="2fa-disable-password"
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-600">
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-500">Authenticator code</Label>
            <Input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              className="text-center font-mono tracking-[0.3em]"
              data-testid="2fa-disable-code"
            />
          </div>
          <div className="flex gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 h-10 text-xs" data-testid="2fa-disable-cancel">Cancel</Button>
            <Button type="submit" disabled={loading || !password || code.length !== 6} className="flex-1 bg-red-600 hover:bg-red-700 text-white h-10 text-xs" data-testid="2fa-disable-confirm">
              {loading ? <Loader2 size={14} className="animate-spin" /> : "Disable 2FA"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
